import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─── Types ──────────────────────────────────────────────────────────────

export type TopologyClassification = 'shared_bank' | 'independent_banks' | 'unknown';
export type TopologyConfidence = 'high' | 'medium' | 'low';
export type TopologySource = 'manual_override' | 'telemetry_correlation' | 'fallback';

export interface TopologyPlantRow {
  plant_id: string;
  property_id: string;
  property_name: string;
  provider_type: string;
  system_count: number;
  battery_kwh_configured: number;
  battery_kwh_total: number;
  battery_topology_override: TopologyClassification | null;
  // Telemetry correlation stats (computed client-side from raw buckets)
  overlapping_buckets: number;
  avg_soc_delta: number | null;
  max_soc_delta: number | null;
  p95_soc_delta: number | null;
  sustained_divergence_count: number;
  // Derived classification
  topology: TopologyClassification;
  topology_confidence: TopologyConfidence;
  topology_source: TopologySource;
  battery_domain_count: number;
  domain_count_approximate: boolean;
  effective_nameplate_kwh: number;
  review_required: boolean;
  // Baseline
  baseline_learned_kwh: number | null;
  baseline_confidence: string | null;
  baseline_status: string | null;
  // Monthly report
  monthly_report_subscribed: boolean;
}

// ─── Fetch ──────────────────────────────────────────────────────────────

async function fetchTopologyData(): Promise<TopologyPlantRow[]> {
  // 1. Get multi-inverter plants
  const { data: plants, error: plantsErr } = await supabase
    .from('plants')
    .select('id, property_id, provider_type, battery_kwh_configured, battery_topology_override')
    .not('battery_kwh_configured', 'is', null)
    .gt('battery_kwh_configured', 0);

  if (plantsErr) throw plantsErr;
  if (!plants?.length) return [];

  // 2. Get system counts per property
  const propertyIds = [...new Set(plants.map((p) => p.property_id))];
  const { data: systems } = await supabase
    .from('energy_systems')
    .select('id, property_id')
    .in('property_id', propertyIds);

  const systemCountByProp = new Map<string, number>();
  for (const s of systems ?? []) {
    systemCountByProp.set(s.property_id, (systemCountByProp.get(s.property_id) ?? 0) + 1);
  }

  // Filter to multi-inverter only
  const multiPlants = plants.filter((p) => (systemCountByProp.get(p.property_id) ?? 1) >= 2);
  if (!multiPlants.length) return [];

  // 3. Get property names + battery_kwh_total
  const multiPropIds = [...new Set(multiPlants.map((p) => p.property_id))];
  const { data: props } = await supabase
    .from('properties')
    .select('id, name, battery_kwh_total')
    .in('id', multiPropIds);
  const propMap = new Map((props ?? []).map((p) => [p.id, p]));

  // 4. Get baselines
  const plantIds = multiPlants.map((p) => p.id);
  const { data: baselines } = await supabase
    .from('battery_capacity_baselines')
    .select('plant_id, learned_capacity_kwh, confidence, status')
    .in('plant_id', plantIds);
  const baselineMap = new Map((baselines ?? []).map((b) => [b.plant_id, b]));

  // 5. Get monthly report subscriptions
  const { data: reportSubs } = await supabase
    .from('customer_report_subscriptions')
    .select('property_id')
    .in('property_id', multiPropIds);
  const reportSet = new Set((reportSubs ?? []).map((r) => r.property_id));

  // 6. Get telemetry stats via RPC (we'll use a raw query approach)
  // For each plant, compute SoC correlation stats from last 3 days
  const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
  const { data: telemetryStats } = await supabase.rpc('admin_battery_topology_stats', {
    p_plant_ids: plantIds,
    p_since: threeDaysAgo,
  });

  const statsMap = new Map<string, {
    overlapping_buckets: number;
    avg_soc_delta: number | null;
    max_soc_delta: number | null;
    p95_soc_delta: number | null;
    sustained_divergence_count: number;
  }>();

  if (telemetryStats) {
    for (const s of telemetryStats as Array<Record<string, unknown>>) {
      statsMap.set(s.plant_id as string, {
        overlapping_buckets: Number(s.overlapping_buckets ?? 0),
        avg_soc_delta: s.avg_soc_delta != null ? Number(s.avg_soc_delta) : null,
        max_soc_delta: s.max_soc_delta != null ? Number(s.max_soc_delta) : null,
        p95_soc_delta: s.p95_soc_delta != null ? Number(s.p95_soc_delta) : null,
        sustained_divergence_count: Number(s.sustained_divergence_count ?? 0),
      });
    }
  }

  // 7. Build rows with classification logic matching battery-topology.ts
  return multiPlants.map((plant) => {
    const prop = propMap.get(plant.property_id);
    const systemCount = systemCountByProp.get(plant.property_id) ?? 2;
    const configKwh = Number(plant.battery_kwh_configured);
    const totalKwh = Number(prop?.battery_kwh_total ?? configKwh);
    const bl = baselineMap.get(plant.id);
    const stats = statsMap.get(plant.id);
    const override = plant.battery_topology_override as TopologyClassification | null;

    // Classify (mirrors battery-topology.ts logic)
    const { topology, confidence, source, domainCount, approximate, reviewRequired } =
      classifyTopology(override, systemCount, stats ?? null);

    const effectiveNameplate = topology === 'shared_bank'
      ? totalKwh
      : totalKwh / domainCount;

    return {
      plant_id: plant.id,
      property_id: plant.property_id,
      property_name: prop?.name ?? 'Unknown',
      provider_type: plant.provider_type ?? 'unknown',
      system_count: systemCount,
      battery_kwh_configured: configKwh,
      battery_kwh_total: totalKwh,
      battery_topology_override: override,
      overlapping_buckets: stats?.overlapping_buckets ?? 0,
      avg_soc_delta: stats?.avg_soc_delta ?? null,
      max_soc_delta: stats?.max_soc_delta ?? null,
      p95_soc_delta: stats?.p95_soc_delta ?? null,
      sustained_divergence_count: stats?.sustained_divergence_count ?? 0,
      topology,
      topology_confidence: confidence,
      topology_source: source,
      battery_domain_count: domainCount,
      domain_count_approximate: approximate,
      effective_nameplate_kwh: Math.round(effectiveNameplate * 100) / 100,
      review_required: reviewRequired,
      baseline_learned_kwh: bl?.learned_capacity_kwh != null ? Number(bl.learned_capacity_kwh) : null,
      baseline_confidence: bl?.confidence ?? null,
      baseline_status: bl?.status ?? null,
      monthly_report_subscribed: reportSet.has(plant.property_id),
    };
  }).sort((a, b) => {
    // review_required first, then by property name
    if (a.review_required !== b.review_required) return a.review_required ? -1 : 1;
    return a.property_name.localeCompare(b.property_name);
  });
}

// ─── Classification (mirrors battery-topology.ts) ───────────────────────

function classifyTopology(
  override: TopologyClassification | null,
  systemCount: number,
  stats: {
    overlapping_buckets: number;
    avg_soc_delta: number | null;
    max_soc_delta: number | null;
    p95_soc_delta: number | null;
    sustained_divergence_count: number;
  } | null,
): {
  topology: TopologyClassification;
  confidence: TopologyConfidence;
  source: TopologySource;
  domainCount: number;
  approximate: boolean;
  reviewRequired: boolean;
} {
  // Manual override
  if (override === 'shared_bank' || override === 'independent_banks') {
    return {
      topology: override,
      confidence: 'high',
      source: 'manual_override',
      domainCount: override === 'shared_bank' ? 1 : systemCount,
      approximate: false,
      reviewRequired: false,
    };
  }

  // Insufficient data
  if (!stats || stats.overlapping_buckets < 20) {
    return {
      topology: 'unknown',
      confidence: 'low',
      source: 'fallback',
      domainCount: systemCount,
      approximate: true,
      reviewRequired: true,
    };
  }

  const avg = stats.avg_soc_delta ?? 0;
  const max = stats.max_soc_delta ?? 0;
  const p95 = stats.p95_soc_delta ?? 0;
  const sustained = stats.sustained_divergence_count;
  const buckets = stats.overlapping_buckets;

  // Shared bank
  if (avg <= 1.0 && max <= 3.0) {
    return {
      topology: 'shared_bank',
      confidence: buckets >= 50 ? 'high' : 'medium',
      source: 'telemetry_correlation',
      domainCount: 1,
      approximate: false,
      reviewRequired: false,
    };
  }

  // Independent banks
  if (avg >= 5.0 || p95 >= 10.0 || sustained >= 3) {
    // For 2-system plants, domain count is exact
    const isApproximate = systemCount > 2;
    return {
      topology: 'independent_banks',
      confidence: isApproximate ? 'medium' : (buckets >= 50 ? 'high' : 'medium'),
      source: 'telemetry_correlation',
      domainCount: systemCount > 2 ? systemCount : 2, // simplified; real detection is server-side
      approximate: isApproximate,
      reviewRequired: isApproximate,
    };
  }

  // Ambiguous
  return {
    topology: 'unknown',
    confidence: 'low',
    source: 'telemetry_correlation',
    domainCount: systemCount,
    approximate: true,
    reviewRequired: true,
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAdminBatteryTopology(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-battery-topology'],
    queryFn: fetchTopologyData,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Override mutation ──────────────────────────────────────────────────

interface SetTopologyOverrideParams {
  plantId: string;
  propertyId: string;
  override: TopologyClassification | null;
  previousKwh: number;
  effectiveKwh: number;
}

async function setTopologyOverride({
  plantId,
  propertyId,
  override,
  previousKwh,
  effectiveKwh,
}: SetTopologyOverrideParams): Promise<void> {
  // Update the plant's topology override
  const { error: updateErr } = await supabase
    .from('plants')
    .update({ battery_topology_override: override })
    .eq('id', plantId);

  if (updateErr) throw updateErr;

  // Log to battery_capacity_changes for audit trail
  const { error: auditErr } = await supabase
    .from('battery_capacity_changes')
    .insert({
      property_id: propertyId,
      previous_kwh: previousKwh,
      new_kwh: effectiveKwh,
      change_source: 'admin_fix',
      reason: `Topology override set to ${override ?? 'auto-detect'}. Effective nameplate changed.`,
    });

  if (auditErr) {
    console.error('[topology-override] audit insert error:', auditErr.message);
  }
}

export function useSetTopologyOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTopologyOverride,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-battery-topology'] });
    },
  });
}
